import { useCallback, useMemo } from 'react';
import * as sentryAppLogger from './sentryAppLogger';

type LogContext = Record<string, unknown>;

export type UseAppLoggerResult = {
  captureException: (error: unknown, context?: LogContext) => void;
  logDebug: (message: string, context?: LogContext) => void;
  logInfo: (message: string, context?: LogContext) => void;
  logWarning: (message: string, context?: LogContext) => void;
  logError: (message: string, context?: LogContext) => void;
  addBreadcrumb: (message: string, context?: LogContext) => void;
  setUser: (user: { id?: string | number | null } | null) => void;
};

export function useAppLogger(): UseAppLoggerResult {
  const captureException = useCallback(
    (error: unknown, context?: LogContext) => sentryAppLogger.captureException(error, context),
    [],
  );

  const logDebug = useCallback(
    (message: string, context?: LogContext) => sentryAppLogger.logDebug(message, context),
    [],
  );

  const logInfo = useCallback(
    (message: string, context?: LogContext) => sentryAppLogger.logInfo(message, context),
    [],
  );

  const logWarning = useCallback(
    (message: string, context?: LogContext) => sentryAppLogger.logWarning(message, context),
    [],
  );

  const logError = useCallback(
    (message: string, context?: LogContext) => sentryAppLogger.logError(message, context),
    [],
  );

  const addBreadcrumb = useCallback(
    (message: string, context?: LogContext) => sentryAppLogger.addBreadcrumb(message, context),
    [],
  );

  const setUser = useCallback(
    (user: { id?: string | number | null } | null) => sentryAppLogger.setUser(user),
    [],
  );

  return useMemo(
    () => ({
      captureException,
      logDebug,
      logInfo,
      logWarning,
      logError,
      addBreadcrumb,
      setUser,
    }),
    [captureException, logDebug, logInfo, logWarning, logError, addBreadcrumb, setUser],
  );
}
